import type { Decision } from "../dashboard/deskLogic";
import { viewerNote as deskViewerNote } from "../dashboard/deskLogic";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The transport manager's dashboard: work waiting, today's trips, the fleet
 * and each route's load. Mirrors apps/dashboard/transport.py.
 */

export interface TodayTrip {
  id: number; route: string; vehicle: string; driver: string; departure: string; arrival: string
  status: "scheduled" | "in_progress" | "completed" | "cancelled" | "delayed"; onboard: number; capacity: number; late_minutes: number
}
export interface FleetVehicle {
  id: number; name: string; registration: string; status: string; seats: number; driver: string
  next_paper: string; next_paper_expiry: string | null; open_repairs: number
}
export interface RouteLoad { id: number; name: string; departure: string; riders: number; waiting: number; capacity: number }
export interface FleetDesk {
  generated_at: string; today: string; viewer_role: string
  work: Decision[]; trips: TodayTrip[]; fleet: FleetVehicle[]; routes: RouteLoad[]
}

/** transportLogic.PAPERS_WARNING_DAYS */
export const PAPERS_WARNING_DAYS = 30;
const DAY_MS = 86_400_000;
const daysUntil = (today: string, day: string) => Math.round((Date.parse(day) - Date.parse(today)) / DAY_MS);

type Tone = { label: string; dot: string };
export function tripTone(trip: Pick<TodayTrip, "status" | "late_minutes">): Tone {
  if (trip.status === "cancelled") return { label: "Cancelled", dot: "bg-gray-400" };
  if (trip.status === "completed") return { label: "Arrived", dot: "bg-emerald-500" };
  if (trip.status === "in_progress") return { label: trip.late_minutes ? `On the road, left ${trip.late_minutes} min late` : "On the road", dot: "bg-sky-500" };
  if (trip.status === "delayed" || trip.late_minutes) return { label: trip.late_minutes ? `Late by ${trip.late_minutes} min` : "Delayed", dot: "bg-red-500" };
  return { label: "To leave", dot: "bg-amber-500" };
}

export function paperTone(vehicle: Pick<FleetVehicle, "next_paper" | "next_paper_expiry">, today: string): Tone {
  if (!vehicle.next_paper_expiry) return { label: "No dates recorded", dot: "bg-gray-300" };
  const days = daysUntil(today, vehicle.next_paper_expiry);
  if (days < 0) return { label: `${vehicle.next_paper} expired`, dot: "bg-red-500" };
  if (days <= PAPERS_WARNING_DAYS) return { label: `${vehicle.next_paper} expires in ${days} ${days === 1 ? "day" : "days"}`, dot: "bg-amber-500" };
  return { label: "In order", dot: "bg-emerald-500" };
}

/** How full a route is, and whether it can take the riders waiting. */
export function routeLoad(route: RouteLoad) {
  const share = route.capacity ? Math.round((route.riders / route.capacity) * 100) : 0;
  const free = Math.max(route.capacity - route.riders, 0);
  return { share, free, full: route.capacity > 0 && route.riders >= route.capacity, overbooked: route.riders + route.waiting > route.capacity };
}

export const tripConfig: RegisterConfig<TodayTrip> = {
  search: (row) => `${row.route} ${row.vehicle} ${row.driver}`,
  views: [
    { key: "all", label: "All" },
    { key: "late", label: "Late", match: (row) => row.status === "delayed" || (row.status === "scheduled" && row.late_minutes > 0) },
    { key: "road", label: "On the road", match: (row) => row.status === "in_progress" },
    { key: "done", label: "Arrived", match: (row) => row.status === "completed" },
  ],
  sorts: [
    { key: "time", label: "Departure", compare: byText((row) => row.departure) },
    { key: "late", label: "Latest running first", compare: reversed(byNumber((row) => row.late_minutes)) },
    { key: "route", label: "Route A–Z", compare: byText((row) => row.route) },
  ],
};

export const fleetConfig = (today: string): RegisterConfig<FleetVehicle> => ({
  search: (row) => `${row.name} ${row.registration} ${row.driver}`,
  views: [
    { key: "all", label: "All" },
    { key: "papers", label: "Papers due", match: (row) => paperTone(row, today).dot !== "bg-emerald-500" && Boolean(row.next_paper_expiry) },
    { key: "workshop", label: "In the workshop", match: (row) => row.status === "maintenance" || row.open_repairs > 0 },
    { key: "no-driver", label: "No driver", match: (row) => !row.driver },
  ],
  sorts: [
    { key: "papers", label: "Papers expiring first", compare: byText((row) => row.next_paper_expiry ?? "9999") },
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
  ],
});

export const viewerNote = (role: string) =>
  role === "transport_manager" ? "" : deskViewerNote(role, "transport_manager", "the transport manager's dashboard").replace(" The decisions listed are the ones your own account may take, and appraisals are those assigned to you.", " Everything here is the whole fleet's.");
